// IE Hub - Special Text Field Component
// Enhanced text input with validation and JSON schema integration
// This is a specialized variant of TextField for custom use cases

import React, { forwardRef, useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import ConditionalContainer from "../layout/ConditionalContainer";
import FieldHelpDialog, {
  FieldHelpContent,
} from "../../components/FieldHelpDialog";
import { UI_MESSAGES } from "../../constants/ui";

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  patternFlags?: string;
}

interface SpecialTextFieldProps
  extends Omit<MuiTextFieldProps, "variant" | "placeholder" | "label"> {
  type?: string;
  variant?: "outlined" | "filled" | "standard";
  size?: "small" | "medium";
  fullWidth?: boolean;
  autoComplete?: string;
  placeholder?: string;
  showPasswordToggle?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  showCharacterCount?: boolean;
  showValidationIcon?: boolean;
  isLoading?: boolean;
  // When true, suppress local (onChange/onBlur) validation and only show parent errors
  deferValidation?: boolean;
  /**
   * Styles applied to the underlying MUI TextField component.
   * Note: the existing `sx` prop styles the surrounding container (legacy behaviour).
   */
  textFieldSx?: MuiTextFieldProps["sx"];
  // Custom UI blocks
  helperTop?: string | undefined; // Above input
  helperAttached?: string | undefined; // Attached block inside container
  helperBottom?: string | undefined; // Below input
  showErrorBelow?: boolean; // controls whether error renders beneath input
  label?: string | undefined; // Rendered above the field (not inside input)
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: "paper" | "outlined" | "plain";
  showErrorInContainer?: boolean;
  // No value checkbox option
  allowNoValue?: boolean;
  noValueCheckboxLabel?: string | undefined;
  noValueFieldName?: string | undefined;
  noValueChecked?: boolean;
  onNoValueChange?: (checked: boolean) => void;
  // Field help dialog
  helpDialog?: FieldHelpContent;
  helpLinkText?: string;

  // Mobile keyboard and input behavior (defaults to normal text keyboard)
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoCapitalize?: "off" | "none" | "sentences" | "words" | "characters";
  autoCorrect?: "on" | "off";
  spellCheck?: boolean;

  // LogRocket privacy masking - when true, adds data-private attribute to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;

  /**
   * When true, enforces 50-char limit and blocks single quotes with standardized alerts.
   * Useful for referenceId/accountNumber fields across the app.
   */
  isAccountField?: boolean;
  /**
   * Throttling for account field alerts in ms. Defaults to 1500.
   */
  accountFieldAlertThrottle?: number;
}

// Styled SpecialTextField with enhanced visual feedback
const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const AttachedBlock = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[900],
  border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const StyledSpecialTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error?: boolean }>(({ theme, error }) => {
  return {
    "& .MuiOutlinedInput-root": {
      // Single-line fields keep a consistent height.
      minHeight: "44px",
      height: "44px",
      alignItems: "center",
      transition: "all 0.2s ease-in-out",
      backgroundColor: theme.palette.background.paper,

      // Default border styling
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },

      // Error state styling - highest priority
      ...(error && {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
      }),

      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : theme.palette.primary.main,
          borderWidth: "2px !important",
        },
      },

      "&.Mui-focused": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.2)}`,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : `${theme.palette.primary.main} !important`,
          borderWidth: "2px !important",
        },
      },

      // MUI error class - ensure it works
      "&.Mui-error": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
      },

      "&.Mui-disabled": {
        backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      },
    },

    // Multiline fields must be allowed to grow (e.g., minRows) and align text to the top.
    "& .MuiOutlinedInput-root.MuiInputBase-multiline, & .MuiOutlinedInput-root.MuiOutlinedInput-multiline":
      {
        height: "auto",
        alignItems: "flex-start",
      },

    "& .MuiInputLabel-outlined": {
      "&.Mui-focused": {
        color: error ? theme.palette.error.main : theme.palette.primary.main,
      },
      "&.Mui-error": {
        color: theme.palette.error.main,
      },
    },

    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: theme.spacing(0.75),
      fontSize: "0.8125rem",
      transition: "color 0.2s ease-in-out",
    },
  };
});

// Character count component
const CharacterCount = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  textAlign: "right",
}));

const SpecialTextField = forwardRef<HTMLInputElement, SpecialTextFieldProps>(
  (
    {
      type = "text",
      variant = "outlined",
      size = "medium",
      fullWidth = true,
      autoComplete = "off",
      showPasswordToggle = false,
      startIcon,
      endIcon,
      validation = {},
      onValidate,
      sx = {},
      textFieldSx,
      InputProps = {},
      inputProps: rawInputProps = {},
      showCharacterCount = false,
      showValidationIcon = true,
      isLoading = false,
      deferValidation = false,
      helperTop,
      helperAttached,
      helperBottom,
      showErrorBelow = true,
      label: fieldLabel,
      value,
      onChange,
      onBlur,
      // ConditionalContainer props
      title,
      description,
      containerVariant = "plain",
      showErrorInContainer = true,
      // No value checkbox props
      allowNoValue = false,
      noValueCheckboxLabel = "I don't have this information",
      noValueFieldName,
      noValueChecked = false,
      onNoValueChange,
      // Field help dialog props
      helpDialog,
      helpLinkText,
      inputMode,
      autoCapitalize = "none",
      autoCorrect = "off",
      spellCheck = false,
      // LogRocket privacy masking - defaults to true
      dataPrivate = true,
      isAccountField = false,
      accountFieldAlertThrottle = 1500,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [hasValue, setHasValue] = useState(false);
    const [isNoValueChecked, setIsNoValueChecked] = useState(noValueChecked);
    const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);

    const lastAccountAlertAtRef = React.useRef<number>(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Sync checkbox state with prop changes
    useEffect(() => {
      setIsNoValueChecked(noValueChecked);
    }, [noValueChecked]);

    // Determine actual input type
    let inputType = type;
    if (type === "password" && showPassword) {
      inputType = "text";
    }

    // Derive inputMode for mobile keyboards; default to normal text keyboard
    const derivedInputMode =
      inputMode ??
      (type === "email"
        ? "email"
        : type === "tel"
          ? "tel"
          : type === "url"
            ? "url"
            : type === "number"
              ? "numeric"
              : "text");

    // Handle password visibility toggle
    const handlePasswordToggle = () => {
      setShowPassword(!showPassword);
    };

    // Handle no value checkbox change
    const handleNoValueCheckboxChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const checked = event.target.checked;
      setIsNoValueChecked(checked);

      // Clear the text field when checkbox is checked
      if (checked) {
        // Clear validation error
        setValidationError("");

        // Clear the input value
        if (onChange) {
          const syntheticEvent = {
            ...event,
            target: {
              ...event.target,
              value: "",
              name: props.name || "",
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      } else {
        // When unchecking, clear validation error to allow user to enter value
        setValidationError("");
      }

      // Notify parent about checkbox state change
      if (onNoValueChange) {
        onNoValueChange(checked);
      }
    };

    // Validation logic
    const validateInput = (inputValue: string) => {
      let error = "";

      // Skip required validation if "no value" checkbox is checked
      if (isNoValueChecked) {
        return true;
      }

      // Required validation
      if (validation.required && (!inputValue || inputValue.trim() === "")) {
        error = "This field is required";
      }

      // Min/Max length validation
      if (
        inputValue &&
        validation.minLength &&
        inputValue.length < validation.minLength
      ) {
        error = `Minimum ${validation.minLength} characters required`;
      }

      if (
        inputValue &&
        validation.maxLength &&
        inputValue.length > validation.maxLength
      ) {
        error = `Maximum ${validation.maxLength} characters allowed`;
      }

      // Pattern validation
      if (inputValue && validation.pattern) {
        const regex = new RegExp(validation.pattern, validation.patternFlags);
        if (!regex.test(inputValue.trim())) {
          error = validation.patternMessage || "Invalid format";
        }
      }

      // Email validation
      if (type === "email" && inputValue) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inputValue)) {
          error = "Please enter a valid email address";
        }
      }

      // URL validation
      if (type === "url" && inputValue) {
        try {
          new URL(inputValue);
        } catch {
          error = "Please enter a valid URL";
        }
      }

      if (!deferValidation) {
        setValidationError(error);

        if (onValidate) {
          onValidate(error === "", error);
        }
      }

      return error === "";
    };

    // Handle input change with validation
    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      let inputValue = event.target.value;

      // Handle account field logic (apostrophe block and length limit)
      if (isAccountField) {
        const hasApostrophe = inputValue.includes("'");
        const isOverLimit = inputValue.length > 50;

        if (hasApostrophe || isOverLimit) {
          const now = Date.now();
          if (
            now - lastAccountAlertAtRef.current >=
            accountFieldAlertThrottle
          ) {
            lastAccountAlertAtRef.current = now;
            const message = isOverLimit
              ? UI_MESSAGES.CHARACTER_LIMIT_REACHED
              : UI_MESSAGES.APOSTROPHE_NOT_ALLOWED;

            try {
              window.dispatchEvent(
                new CustomEvent("iehub-alert", {
                  detail: {
                    severity: "warning",
                    message: message,
                    variant: "filled",
                    duration: 3000,
                  },
                })
              );
            } catch {
              // no-op
            }
          }
          // Sanitize: remove apostrophes and slice to 50
          inputValue = inputValue.replace(/'/g, "").slice(0, 50);
          event.target.value = inputValue;
        }
      }

      // For tel type, filter to only allow digits, plus sign, and spaces
      if (type === "tel") {
        inputValue = inputValue.replace(/[^0-9+\s]/g, "");
        // Update the event target value with filtered value
        (event.target as HTMLInputElement).value = inputValue;
      }

      setHasValue(inputValue.length > 0);

      // Validate on change if enabled and validation rules exist
      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(inputValue);
      }

      if (onChange) {
        onChange(event);
      }
    };

    const handlePaste = (
      event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const pastedData = event.clipboardData.getData("text");
      const maxLength = isAccountField ? 50 : validation.maxLength;

      if (maxLength) {
        const target = event.target as HTMLInputElement;
        const currentLength = target.value.length;
        const selectionStart = target.selectionStart || 0;
        const selectionEnd = target.selectionEnd || 0;
        const newLength =
          currentLength - (selectionEnd - selectionStart) + pastedData.length;

        if (newLength > maxLength) {
          const now = Date.now();
          if (
            now - lastAccountAlertAtRef.current >=
            accountFieldAlertThrottle
          ) {
            lastAccountAlertAtRef.current = now;

            try {
              window.dispatchEvent(
                new CustomEvent("iehub-alert", {
                  detail: {
                    severity: "warning",
                    message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                    variant: "filled",
                    duration: 3000,
                  },
                })
              );
            } catch {
              // no-op
            }
          }
        }
      }

      if (props.onPaste) {
        (props.onPaste as any)(event);
      }
    };

    // Handle blur for validation
    const handleBlur = (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const inputValue = event.target.value;
      if (!deferValidation) {
        validateInput(inputValue);
      }

      if (onBlur) {
        onBlur(event);
      }
    };

    const handleFocus = () => {
      // Focus handling
    };

    // Handle help dialog
    const handleHelpDialogOpen = () => {
      setIsHelpDialogOpen(true);
    };

    const handleHelpDialogClose = () => {
      setIsHelpDialogOpen(false);
    };

    // Determine validation icon
    const getValidationIcon = () => {
      if (deferValidation || !showValidationIcon || !hasValue) return null;

      if (validationError) {
        return <ErrorIcon color="error" fontSize="small" />;
      }

      // Removed green checkmark - no icon for valid state
      return null;
    };

    // Build MUI InputProps (adornments etc.)
    const muiInputProps: any = {
      ...InputProps,
      startAdornment: startIcon ? (
        <InputAdornment position="start" sx={{ color: "action.active" }}>
          {startIcon}
        </InputAdornment>
      ) : (
        InputProps.startAdornment
      ),

      endAdornment: (
        <>
          {/* Validation icon */}
          {getValidationIcon() && (
            <InputAdornment position="end">
              {getValidationIcon()}
            </InputAdornment>
          )}

          {/* Password toggle */}
          {type === "password" && showPasswordToggle && (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handlePasswordToggle}
                edge="end"
                size="small"
                sx={{
                  "&:hover": (theme) => ({
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  }),
                }}
              >
                {showPassword ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          )}

          {/* Custom end icon */}
          {endIcon && (
            <InputAdornment position="end" sx={{ color: "action.active" }}>
              {endIcon}
            </InputAdornment>
          )}

          {InputProps.endAdornment}
        </>
      ),
    };

    const handleNumberWheel = (event: React.WheelEvent<HTMLInputElement>) => {
      // Preserve any consumer-provided wheel handler first
      const rawOnWheel = (rawInputProps as { onWheel?: unknown } | undefined)
        ?.onWheel;
      if (typeof rawOnWheel === "function") {
        rawOnWheel(event);
      }

      // On many browsers, wheel over <input type="number"> changes the value
      // and prevents the user from scrolling the page/container.
      // Blurring the input restores normal scrolling.
      event.currentTarget.blur();
    };

    // Build HTML input props (keyboard behavior)
    const htmlInputProps: any = {
      ...(rawInputProps as any),
      inputMode: derivedInputMode,
      autoCapitalize,
      autoCorrect,
      spellCheck,
      readOnly: Boolean((rawInputProps as any)?.readOnly),
      // LogRocket privacy masking - add data-private attribute for PII fields
      ...(dataPrivate && { "data-private": true }),
      ...(inputType === "number" ? { onWheel: handleNumberWheel } : {}),
      // Set maxLength to 50 for account fields if not already specified
      ...(isAccountField &&
        !rawInputProps.hasOwnProperty("maxLength") && { maxLength: 50 }),
    };

    const textFieldValue = value !== undefined ? value : "";
    const characterCount = String(textFieldValue).length;

    // Mask value when field is disabled - show empty but keep actual value intact
    const isFieldDisabled = Boolean(
      props.disabled || (isNoValueChecked && allowNoValue)
    );
    // Always return a defined value (empty string ONLY when checkbox is checked) to avoid controlled/uncontrolled warning
    // Keep the field value intact when just disabled (not checkbox-related)
    const displayValue =
      isNoValueChecked && allowNoValue ? "" : value !== undefined ? value : "";
    // Only control the value when checkbox is checked (to force blank) or when a controlled value is provided by parent
    const controlledValueProps =
      isNoValueChecked || value !== undefined
        ? { value: displayValue }
        : ({} as any);

    // Always handle errors within SpecialTextField, not in container

    // Content to render inside ConditionalContainer
    const textFieldContent = (
      <Wrapper sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 1.5 }}>
        <Wrapper sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {fieldLabel && !title && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500, color: "primary.main", fontSize: isMobile ? '14px' : '16px',
                lineHeight: isMobile ? '20px' : '22px'
              }}
            >
              {fieldLabel}
              {validation.required && !isNoValueChecked && (
                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>
          )}
          {helperTop && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
              {helperTop}
            </Typography>
          )}
          {/* Help Link (shows below helperTop if both exist) */}
          {helpDialog && helpLinkText && (
            <Box
              onClick={handleHelpDialogOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                width: "fit-content",
                "&:hover": {
                  "& .help-text": {
                    color: "primary.dark",
                    textDecoration: "underline",
                  },
                },
              }}
            >
              <Typography
                className="help-text"
                variant="body2"
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                  fontSize: isMobile ? '12px' : '14px',
                  lineHeight: isMobile ? '16px' : '20px',
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {helpLinkText}
              </Typography>
              <HelpOutlineIcon
                sx={{
                  fontSize: "1.125rem",
                  color: "primary.main",
                }}
              />
            </Box>
          )}
        </Wrapper>
        {/* Error message above input field (hide if checkbox is checked) */}
        {!isNoValueChecked &&
          ((!deferValidation && validationError) || props.error) && (
            <Typography
              variant="body2"
              color="error"
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              {(!deferValidation && validationError) ||
                props.helperText ||
                "This field is required"}
            </Typography>
          )}
        <StyledSpecialTextField
          ref={ref}
          type={inputType}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          autoComplete={autoComplete}
          margin="none"
          {...props}
          required={false}
          error={
            !isNoValueChecked &&
            ((!deferValidation && !!validationError) || (props.error ?? false))
          }
          helperText={
            !deferValidation && validationError ? "" : props.helperText || ""
          } // Allow MUI helperText when no local validation error
          FormHelperTextProps={{
            sx: { display: "none", margin: 0, height: 0 },
          }} // Hide helper text completely and remove space
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onFocus={handleFocus}
          InputProps={muiInputProps}
          inputProps={htmlInputProps}
          {...controlledValueProps}
          disabled={isFieldDisabled}
          className={
            !isNoValueChecked && !deferValidation && validationError
              ? "ie-hub-field-error"
              : ""
          }
          sx={[
            {
              marginBottom: 1.5, // 12px (1.5 * 8px)
              "& .MuiInputBase-input": {
                fontSize: size === "small" ? "0.875rem" : "1rem",
              },
              // Force red border when there's a validation error
              "&.ie-hub-field-error": {
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D5391F !important",
                    borderWidth: "2px !important",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D5391F !important",
                    borderWidth: "2px !important",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D5391F !important",
                    borderWidth: "2px !important",
                  },
                  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D5391F !important",
                    borderWidth: "2px !important",
                  },
                },
              },
              // Fallback approach (only apply error styling if checkbox is not checked)
              ...(!isNoValueChecked && !deferValidation && !!validationError
                ? {
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D5391F !important",
                        borderWidth: "2px !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D5391F !important",
                        borderWidth: "2px !important",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D5391F !important",
                        borderWidth: "2px !important",
                      },
                      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D5391F !important",
                        borderWidth: "2px !important",
                      },
                    },
                  }
                : {}),
            },
            textFieldSx,
          ]}
        />
        {helperAttached && <AttachedBlock>{helperAttached}</AttachedBlock>}
        {/* No value checkbox option */}
        {allowNoValue && (
          <Box sx={{ mt: -0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNoValueChecked}
                  onChange={handleNoValueCheckboxChange}
                  disabled={props.disabled || false}
                  {...(noValueFieldName && { name: noValueFieldName })}
                  sx={{
                    color: "text.secondary",
                    // mt: isMobile ? 1.5 : 1.5,
                    // padding: "4px 9px",
                    padding: "0px",
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                    pointerEvents: "auto", // Ensure checkbox is always clickable unless disabled
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    userSelect: "none",
                    cursor: props.disabled ? "not-allowed" : "pointer",
                  }}
                >
                  {noValueCheckboxLabel}
                </Typography>
              }
              sx={{
                ml: 0,
                mt: 0,
                mb: 0,
                cursor: props.disabled ? "not-allowed" : "pointer",
              }}
            />
          </Box>
        )}
        {showCharacterCount &&
          validation.maxLength &&
          characterCount >= validation.maxLength && (
            <CharacterCount
              color={
                characterCount > validation.maxLength
                  ? "error"
                  : "textSecondary"
              }
            >
              {characterCount}/{validation.maxLength}
            </CharacterCount>
          )}
        {helperBottom && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: validationError ? 0.75 : 1.25, mb: 0 }}
          >
            {helperBottom}
          </Typography>
        )}
      </Wrapper>
    );

    const containerProps: any = {
      ifError: false, // Never show error in container
      variant: containerVariant,
      sx: {
        ...(sx as Record<string, unknown>),
        // Add red left border when there's a validation error (only if checkbox is not checked)
        ...(!isNoValueChecked &&
          !deferValidation &&
          validationError && {
          borderLeft: "4px solid",
          borderLeftColor: "error.main",
          paddingLeft: 1.5,
          paddingBottom: 0, // Reduce bottom padding to minimize border height
          minHeight: "auto",
          height: "fit-content",
          maxHeight: "90%", // or whatever height you prefer
        }),
        // Ensure consistent width regardless of error state
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch", // Changed from flex-start to stretch to maintain full width
        gap: 1.5,
        // Reduce overall container height when in error state (only if checkbox is not checked)
        ...(!isNoValueChecked &&
          !deferValidation &&
          validationError && {
          paddingTop: 0,
          marginBottom: 0,
        }),
      },
    };

    if (title || (fieldLabel && title !== undefined)) {
      containerProps.title = title || fieldLabel;
    }

    if (description) {
      containerProps.description = description;
    }

    // Remove container error message logic since we handle errors in SpecialTextField
    // if (containerErrorMessage) {
    //   containerProps.errorMessage = containerErrorMessage;
    // }

    return (
      <>
        <ConditionalContainer {...containerProps}>
          {textFieldContent}
        </ConditionalContainer>

        {/* Field Help Dialog */}
        {helpDialog && (
          <FieldHelpDialog
            open={isHelpDialogOpen}
            onClose={handleHelpDialogClose}
            content={helpDialog}
            zIndex={1400}
          />
        )}
      </>
    );
  }
);

SpecialTextField.displayName = "SpecialTextField";

export default SpecialTextField;
